import React, { useState, useEffect } from 'react';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';
import './Services.css';

const Services = () => {
  const { isTech } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'package',
    base_price_car: '',
    base_price_truck: '',
    duration_minutes: 60,
    is_active: false
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch('/api/services');
      const data = await response.json();
      setServices(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingService
        ? `/api/services/${editingService.id}`
        : '/api/services';
      const method = editingService ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowModal(false);
        resetForm();
        fetchServices();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description || '',
      category: service.category || 'package',
      base_price_car: service.base_price_car || '',
      base_price_truck: service.base_price_truck || '',
      duration_minutes: service.duration_minutes || 60,
      is_active: service.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Move this service to trash? You can restore it later from the Trash page.')) {
      return;
    }

    try {
      const response = await fetch(`/api/services/${serviceId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        fetchServices();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleMove = async (serviceId, direction) => {
    try {
      const response = await fetch(`/api/services/${serviceId}/move`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ direction })
      });

      if (response.ok) {
        fetchServices();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'package',
      base_price_car: '',
      base_price_truck: '',
      duration_minutes: 60,
      is_active: false
    });
    setEditingService(null);
  };

  const formatDuration = (minutes) => {
    const hours = minutes / 60;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  };

  // A service can only move within its own group — same category and same
  // active/draft status — matching how the backend resequences sort_order.
  const canMoveUp = (list, index) => index > 0 && list[index - 1].is_active === list[index].is_active;
  const canMoveDown = (list, index) => index < list.length - 1 && list[index + 1].is_active === list[index].is_active;

  const handleGoLive = async () => {
    if (!editingService) return;
    try {
      const response = await fetch(`/api/services/${editingService.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, is_active: true })
      });

      if (response.ok) {
        setShowModal(false);
        resetForm();
        fetchServices();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="services-page">
      <div className="page-header">
        <h1 className="page-header__title">Services</h1>
        {!isTech() && (
          <div className="page-header__actions">
            <button className="btn btn--primary" onClick={() => { resetForm(); setShowModal(true); }}>
              + Add Service
            </button>
          </div>
        )}
      </div>

      {/* Packages Section */}
      <h2 className="services-section-title">Packages</h2>
      <div className="services-grid">
        {services.filter(s => s.category === 'package').map((service, index, list) => (
          <div key={service.id} className={`service-card ${!service.is_active ? 'service-card--inactive' : ''}`}>
            <div className="service-card__header">
              <h3 className="service-card__name">{service.name}</h3>
              {!service.is_active && (
                <span className="service-card__inactive-badge">Inactive</span>
              )}
            </div>

            <p className="service-card__description">
              {service.description || 'No description'}
            </p>

            <div className="service-card__pricing">
              <div className="service-card__price-item">
                <span className="service-card__price-label">Cars</span>
                <span className="service-card__price-value">
                  ${parseFloat(service.base_price_car || 0).toFixed(2)}
                </span>
              </div>
              <div className="service-card__price-item">
                <span className="service-card__price-label">Trucks/SUVs</span>
                <span className="service-card__price-value">
                  ${parseFloat(service.base_price_truck || 0).toFixed(2)}
                </span>
              </div>
            </div>

            <div className="service-card__duration">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>{formatDuration(service.duration_minutes)}</span>
            </div>

            {!isTech() && (
              <div className="service-card__actions">
                <button
                  className="btn btn--secondary btn--small service-card__move-btn"
                  disabled={!canMoveUp(list, index)}
                  onClick={() => handleMove(service.id, 'up')}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  className="btn btn--secondary btn--small service-card__move-btn"
                  disabled={!canMoveDown(list, index)}
                  onClick={() => handleMove(service.id, 'down')}
                  title="Move down"
                >
                  ↓
                </button>
                <button className="btn btn--secondary btn--small" onClick={() => handleEdit(service)}>
                  Edit
                </button>
                <button className="btn btn--danger btn--small" onClick={() => handleDelete(service.id)}>
                  Delete
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add-ons Section */}
      <h2 className="services-section-title" style={{ marginTop: 'var(--spacing-xl)' }}>Add-ons</h2>
      <div className="services-grid">
        {services.filter(s => s.category === 'addon').map((service, index, list) => (
          <div key={service.id} className={`service-card ${!service.is_active ? 'service-card--inactive' : ''}`}>
            <div className="service-card__header">
              <h3 className="service-card__name">{service.name}</h3>
              {!service.is_active && (
                <span className="service-card__inactive-badge">Inactive</span>
              )}
            </div>

            <p className="service-card__description">
              {service.description || 'No description'}
            </p>

            <div className="service-card__pricing">
              <div className="service-card__price-item">
                <span className="service-card__price-label">Cars</span>
                <span className="service-card__price-value">
                  ${parseFloat(service.base_price_car || 0).toFixed(2)}
                </span>
              </div>
              <div className="service-card__price-item">
                <span className="service-card__price-label">Trucks/SUVs</span>
                <span className="service-card__price-value">
                  ${parseFloat(service.base_price_truck || 0).toFixed(2)}
                </span>
              </div>
            </div>

            <div className="service-card__duration">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>{formatDuration(service.duration_minutes)}</span>
            </div>

            {!isTech() && (
              <div className="service-card__actions">
                <button
                  className="btn btn--secondary btn--small service-card__move-btn"
                  disabled={!canMoveUp(list, index)}
                  onClick={() => handleMove(service.id, 'up')}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  className="btn btn--secondary btn--small service-card__move-btn"
                  disabled={!canMoveDown(list, index)}
                  onClick={() => handleMove(service.id, 'down')}
                  title="Move down"
                >
                  ↓
                </button>
                <button className="btn btn--secondary btn--small" onClick={() => handleEdit(service)}>
                  Edit
                </button>
                <button className="btn btn--danger btn--small" onClick={() => handleDelete(service.id)}>
                  Delete
                </button>
              </div>
            )}
          </div>
        ))}
        {services.filter(s => s.category === 'addon').length === 0 && (
          <div className="empty-state empty-state--small">
            <p>No add-ons yet</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingService ? 'Edit Service' : 'Add Service'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Type *</label>
            <div className="category-selector">
              <button
                type="button"
                className={`category-btn ${formData.category === 'package' ? 'category-btn--active' : ''}`}
                onClick={() => setFormData({ ...formData, category: 'package' })}
              >
                Package
              </button>
              <button
                type="button"
                className={`category-btn ${formData.category === 'addon' ? 'category-btn--active' : ''}`}
                onClick={() => setFormData({ ...formData, category: 'addon' })}
              >
                Add-on
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Service Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={formData.category === 'addon' ? 'e.g., Engine Bay Cleaning' : 'e.g., Full Detail'}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what's included in this service"
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Price (Cars) *</label>
              <input
                type="number"
                className="form-input"
                value={formData.base_price_car}
                onChange={(e) => setFormData({ ...formData, base_price_car: e.target.value })}
                step="0.01"
                min="0"
                placeholder="0.00"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Price (Trucks/SUVs) *</label>
              <input
                type="number"
                className="form-input"
                value={formData.base_price_truck}
                onChange={(e) => setFormData({ ...formData, base_price_truck: e.target.value })}
                step="0.01"
                min="0"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Duration (hours)</label>
              <input
                type="number"
                className="form-input"
                value={formData.duration_minutes / 60}
                onChange={(e) => setFormData({ ...formData, duration_minutes: Math.round(parseFloat(e.target.value || 0) * 60) })}
                min="0.25"
                step="0.25"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </button>
            {editingService && !formData.is_active && (
              <button type="button" className="btn btn--primary" onClick={handleGoLive}>
                🚀 Go Live
              </button>
            )}
            <button type="submit" className="btn btn--primary">
              {editingService ? 'Save Changes' : 'Add Service'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Services;
